import { useEffect, useState } from 'react';
import { api } from '../api/endpoints';
import type { CustomerCard, Pool, Station } from '../api/types';
import {
  formatBalance,
  formatMinutes,
  formatMoney,
  poolForStationType,
  POOL_LABEL,
  STATION_TYPE_LABEL,
} from '../lib/format';
import { Ps5RatePicker, usePs5Rates } from './Ps5RatePicker';
import { useToast } from './Toast';
import { Badge, Button, EmptyState, ErrorNote, Modal, Spinner } from './ui';

// Seating from the customer's side: they are standing at the counter with
// their card open, and staff pick the seat. The mirror of StartSessionDialog,
// which starts from the seat — same endpoint, same rules. Each free station
// says which pool pays for it and what this customer has in that pool; when
// that is nothing, the row offers the right recharge instead of a dead end.
export function SeatCustomerDialog({
  customer,
  onClose,
  onStarted,
  onRecharge,
}: {
  customer: CustomerCard;
  onClose: () => void;
  onStarted: () => void;
  /** Opens the recharge dialog on this card, for the pool a seat needs. */
  onRecharge: (pool: Pool) => void;
}) {
  const toast = useToast();
  const [stations, setStations] = useState<Station[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState<string | null>(null);
  // A PS5 needs players × duration before it can start, so picking one opens
  // the rate picker instead of starting straight away.
  const [ps5Station, setPs5Station] = useState<Station | null>(null);
  const ps5 = usePs5Rates(ps5Station !== null);

  useEffect(() => {
    const controller = new AbortController();
    api.stations
      .list(controller.signal)
      .then(setStations)
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setError(err.message);
      });
    return () => controller.abort();
  }, []);

  const secondsIn = (pool: Pool) =>
    pool === 'simulator'
      ? (customer.wallet.simulator_seconds_remaining ?? customer.wallet.simulator_minutes * 60)
      : (customer.wallet.general_seconds_remaining ?? customer.wallet.general_minutes * 60);

  const free = (stations ?? []).filter(
    (station) => !station.active_session && station.status !== 'maintenance',
  );
  const busyCount = (stations?.length ?? 0) - free.length;

  const start = async (station: Station) => {
    setStarting(station.station_id);
    setError(null);
    try {
      const result = await api.sessions.start(
        station.station_id,
        customer.customer_id,
        station.station_type === 'ps5' ? ps5.rate?.ps5_pricing_id : undefined,
      );
      if (station.station_type === 'ps5') {
        toast.success(
          `${customer.name}'s group started on ${station.name} — ${formatMinutes(result.minutes_remaining)}, ${formatMoney(ps5.rate?.price)} on their tab.`,
        );
      } else if (result.agent_online) {
        toast.success(`${customer.name} started on ${station.name}.`);
      } else {
        // Billing has begun even though the PC has not been told to unlock.
        toast.info(
          `${customer.name} is started on ${station.name}, but that PC is not reporting in — the desktop stays locked until its agent checks in.`,
        );
      }
      onStarted();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setStarting(null);
    }
  };

  if (ps5Station) {
    return (
      <Modal title={`Start ${customer.name}'s group on ${ps5Station.name}`} onClose={onClose}>
        <div className="space-y-4">
          <p className="text-sm text-slate-400">
            Pick the group size and how long. The price goes on {customer.name}'s tab — no
            wallet time is used.
          </p>
          {error && <ErrorNote>{error}</ErrorNote>}
          <Ps5RatePicker choice={ps5} />
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              className="flex-1"
              disabled={!ps5.rate || starting !== null}
              onClick={() => start(ps5Station)}
            >
              {starting ? 'Starting…' : ps5.rate ? `Start · ${formatMoney(ps5.rate.price)}` : 'Pick players and how long'}
            </Button>
            <Button
              variant="secondary"
              className="flex-1"
              disabled={starting !== null}
              onClick={() => setPs5Station(null)}
            >
              Back to stations
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={`Seat ${customer.name}`} onClose={onClose} wide>
      <div className="space-y-4">
        {/* Both pools, so staff see at a glance which seats they can afford. */}
        <div className="flex flex-wrap gap-2">
          {(['general', 'simulator'] as Pool[]).map((pool) => (
            <Badge key={pool} tone={secondsIn(pool) > 0 ? 'green' : 'slate'}>
              {POOL_LABEL[pool]} {formatBalance(secondsIn(pool))}
            </Badge>
          ))}
        </div>

        {error && <ErrorNote>{error}</ErrorNote>}

        {stations === null && !error ? (
          <Spinner label="Loading stations…" />
        ) : free.length === 0 ? (
          <EmptyState title="Every station is taken.">
            Nothing is free right now — stop a session on the floor first.
          </EmptyState>
        ) : (
          <ul className="divide-y divide-slate-800 rounded-lg border border-slate-800">
            {free.map((station) => {
              const isPs5 = station.station_type === 'ps5';
              const pool = poolForStationType(station.station_type) as Pool | null;
              const balance = pool ? secondsIn(pool) : 0;
              // A PS5 is paid on the tab, so any active account can start one.
              const affordable = isPs5 || balance > 0;
              const offline = !isPs5 && station.agent.paired && !station.agent.online;
              return (
                <li
                  key={station.station_id}
                  className="flex flex-wrap items-center justify-between gap-3 px-3 py-3"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-slate-100">
                      {station.name}{' '}
                      <span className="text-xs font-normal text-slate-500">
                        {STATION_TYPE_LABEL[station.station_type] ?? station.station_type}
                      </span>
                    </p>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {isPs5 ? (
                        <Badge tone="sky">Paid on the tab</Badge>
                      ) : (
                        pool && (
                          <Badge tone={affordable ? 'sky' : 'amber'}>
                            Pays from {POOL_LABEL[pool]} · {formatBalance(balance)}
                          </Badge>
                        )
                      )}
                      {offline && <Badge tone="amber">Agent offline</Badge>}
                      {!isPs5 && !station.agent.paired && <Badge tone="slate">Not paired</Badge>}
                    </div>
                  </div>
                  {affordable ? (
                    <Button
                      disabled={starting !== null || !customer.is_active}
                      onClick={() => (isPs5 ? setPs5Station(station) : start(station))}
                    >
                      {starting === station.station_id ? 'Starting…' : isPs5 ? 'Pick group' : 'Start'}
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      onClick={() => {
                        onClose();
                        onRecharge(pool ?? 'general');
                      }}
                    >
                      Recharge {pool === 'simulator' ? 'simulator' : 'PC'}
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {busyCount > 0 && (
          <p className="text-xs text-slate-500">
            {busyCount} station{busyCount === 1 ? ' is' : 's are'} in use or under maintenance
            and not listed.
          </p>
        )}
      </div>
    </Modal>
  );
}
